import { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { api, API } from "@/lib/api";

const ChatAiContext = createContext(null);
export const useChatAi = () => useContext(ChatAiContext);

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `c_${Date.now()}`);
const EXPIRE_MS = 120000; // 120s: oltre questo tempo lontani dalla chat si riparte da una nuova conversazione

export function ChatAiProvider({ children }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(newId());
  const [saved, setSaved] = useState(false);
  const sessionRef = useRef(sessionId);
  const leftAtRef = useRef(null);
  useEffect(() => { sessionRef.current = sessionId; }, [sessionId]);

  const startNew = useCallback(() => {
    const id = newId();
    sessionRef.current = id;
    setSessionId(id);
    setMessages([]);
    setSaved(false);
    setInput("");
    setLoading(false);
  }, []);

  // La richiesta continua in background anche cambiando sezione (il provider resta montato).
  const ask = useCallback(async (text) => {
    const q = ((text ?? "") || input).trim();
    if (!q || loading) return;
    const mySession = sessionRef.current;
    const asstKey = newId();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q, _k: newId(), ts: new Date().toISOString() },
      { role: "assistant", content: "", streaming: true, _k: asstKey, ts: new Date().toISOString() }]);
    setLoading(true);
    const patch = (updater) => setMessages((m) => m.map((x) => (x._k === asstKey ? updater(x) : x)));
    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ question: q, session_id: mySession }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let acc = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (sessionRef.current !== mySession) { reader.cancel(); return; }
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let evt;
          try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (evt.delta) {
            acc += evt.delta;
            patch((x) => ({ ...x, content: acc }));
          } else if (evt.done) {
            patch((x) => ({ ...x, content: acc, streaming: false,
              action_result: evt.action_result, pending_action: evt.pending_action, attachments: evt.attachments }));
          } else if (evt.error) {
            patch((x) => ({ ...x, content: acc || evt.error, streaming: false }));
          }
        }
      }
      patch((x) => ({ ...x, streaming: false }));
      setSaved(true);
    } catch {
      if (sessionRef.current !== mySession) return;
      patch((x) => ({ ...x, content: "Si è verificato un errore. Riprova.", streaming: false }));
    } finally {
      if (sessionRef.current === mySession) setLoading(false);
    }
  }, [input, loading]);

  const confirmAction = useCallback(async (msg) => {
    const pa = msg.pending_action;
    if (!pa || pa.status !== "pending") return;
    setMessages((m) => m.map((x) => (x._k === msg._k ? { ...x, pending_action: { ...pa, status: "working", error: null } } : x)));
    try {
      const r = await api.post("/chat/confirm-action", { action_id: pa.id, session_id: sessionRef.current });
      setMessages((m) => m.map((x) => (x._k === msg._k ? { ...x, pending_action: { ...pa, status: "done", result_summary: r.data.summary } } : x)));
    } catch (e) {
      const detail = e?.response?.data?.detail || "Operazione non riuscita.";
      setMessages((m) => m.map((x) => (x._k === msg._k ? { ...x, pending_action: { ...pa, status: "pending", error: detail } } : x)));
    }
  }, []);

  const cancelAction = useCallback(async (msg) => {
    const pa = msg.pending_action;
    if (!pa) return;
    try { await api.post("/chat/cancel-action", { action_id: pa.id, session_id: sessionRef.current }); } catch (e) { console.debug("cancel", e); }
    setMessages((m) => m.map((x) => (x._k === msg._k ? { ...x, pending_action: { ...pa, status: "cancelled" } } : x)));
  }, []);

  const openConversation = useCallback(async (conv) => {
    sessionRef.current = conv.id;
    setSessionId(conv.id);
    setSaved(true);
    try {
      const r = await api.get("/chat/history", { params: { session_id: conv.id } });
      if (sessionRef.current !== conv.id) return;
      setMessages(r.data.map((m) => ({ ...m, _k: newId() })));
    } catch { setMessages([]); }
  }, []);

  // Chiamato all'uscita dalla pagina Chat: memorizza l'istante di uscita.
  const markLeft = useCallback(() => { leftAtRef.current = Date.now(); }, []);

  // Chiamato all'ingresso nella pagina Chat: se sono passati >120s dall'uscita, nuova conversazione.
  const enter = useCallback(() => {
    if (leftAtRef.current && Date.now() - leftAtRef.current > EXPIRE_MS) {
      startNew();
    }
    leftAtRef.current = null;
  }, [startNew]);

  const value = {
    messages, setMessages, input, setInput, loading, sessionId, saved,
    ask, confirmAction, cancelAction, startNew, openConversation, markLeft, enter,
  };

  return <ChatAiContext.Provider value={value}>{children}</ChatAiContext.Provider>;
}
